import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment-timezone';
import { catchError, filter, of, Subscription, switchMap, take, tap } from 'rxjs';
import { NgbCalendar, NgbDate, NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';

import { Schedule } from '@shared/schedule/models';
import {
  ConfirmationModalComponent, ConfirmationModalResult,
  CustomAdapter,
  CustomDateParserFormatterProvider,
} from '@tablesready/common-ui';
import { ScheduleService as ScheduleApiService } from '../../../services';
import { timepickerValidator } from './timepicker-validator';
import { AddEditScheduleComponent } from './modals/add-edit-schedule/add-edit-schedule.component';
import { ScheduleService } from './schedules.service';
import { IException, IExceptionForm, IScheduleForm } from './models';
import { exceptionValidator } from './exception-validator';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-schedules',
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.scss'],
  providers: [
    {
      provide: NgbDateAdapter,
      useClass: CustomAdapter,
    },
    CustomDateParserFormatterProvider,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulesComponent implements OnInit, OnDestroy {
  icon = AppIcon;
  color = AppIconColor;
  size = AppIconSize;
  schedules: IScheduleForm[] = [];
  timezone: string;
  extraDate = '';
  showExceptions: object = {};
  showCalendar: object = {};
  dateOptions: {
    minDate: NgbDate;
  };

  restaurantHoursForm: UntypedFormGroup;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly scheduleService: ScheduleService,
    private readonly fb: UntypedFormBuilder,
    private readonly modalController: FrontendModalService,
    private readonly calendar: NgbCalendar,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly scheduleApiService: ScheduleApiService,
  ) {}

  ngOnInit(): void {
    this.dateOptions = { minDate: this.calendar.getToday() };
    this.timezone = this.authRestaurantService.get().location.timezone;
    const scheduleSubscription = this.scheduleApiService.selectAll()
      .pipe(
        tap((schedules) => {
          this.restaurantHoursForm = this.fb.group({ schedules: this.fb.array([]) });
          this.schedules = (schedules || []).map(schedule => this.scheduleService.convertSchedule(schedule, this.timezone));
          this.schedules.forEach((schedule, schIndex) => {
            this.addSchedule(schedule);
            if (schedule?.exceptions.length) {
              schedule.exceptions.forEach((exception) => {
                this.addException(schIndex, exception);
              });
            }
          });
          this.cdr.detectChanges();
        }),
        catchError((error) => {
          this.alertsService.addError(String(error.error.error));

          return of(null);
        }),
      )
      .subscribe();
    this.subscriptions.add(scheduleSubscription);
  }

  updateExceptionsModel() {
    this.schedules
      .forEach((schedule, i) => {
        const { exceptions } = schedule;

        if (exceptions?.length) {
          exceptions.forEach((exception, index) => {
            this.restaurantHoursForm.get(`schedules.${i}.exceptions.${index}`).updateValueAndValidity();
          });
        }
      });
  }

  get schedulesArray(): UntypedFormArray {
    return this.restaurantHoursForm.get('schedules') as UntypedFormArray;
  }

  isDefaultScheduleException(schIndex: number, excIndex: number): boolean {
    const currentSchedule = this.schedulesArray.value[schIndex];
    const defaultSchedule = this.schedulesArray.value.find(schedule => schedule.default);
    const exception = currentSchedule.exceptions[excIndex];
    const exceptionIndexInDefaultSchedule = defaultSchedule.exceptions.find((defaultException, index) => (defaultException.closed && defaultException.closed === exception.closed) ||
          (
            index === excIndex &&
            defaultException._id === exception._id &&
            defaultException.hoursFirstValueFirstArray.hours === exception.hoursFirstValueFirstArray.hours &&
            defaultException.hoursFirstValueFirstArray.minutes === exception.hoursFirstValueFirstArray.minutes &&
            defaultException.hoursSecondValueFirstArray.hours === exception.hoursSecondValueFirstArray.hours &&
            defaultException.hoursSecondValueFirstArray.minutes === exception.hoursSecondValueFirstArray.minutes &&
            defaultException.nextDay === exception.nextDay
          ) && defaultException.date === exception.date);

    return !currentSchedule.default && Boolean(exceptionIndexInDefaultSchedule);
  }

  newSchedule(schedule, isNewSchedule = false): UntypedFormGroup {
    const scheduleExceptions = isNewSchedule ? schedule.exceptions : [];

    return this.fb.group({
      default: [schedule.default || false],
      name: [schedule.name, [Validators.required]],
      exceptions: this.fb.array(scheduleExceptions),
      restaurant: [schedule.restaurant],
      _id: [schedule._id],
      extraDate: [''],
    });
  }

  addSchedule(schedule, isNewSchedule = false): void {
    this.schedulesArray.push(this.newSchedule(schedule, isNewSchedule));
  }

  exceptionsArray(schIndex: number): UntypedFormArray {
    return this.schedulesArray.at(schIndex).get('exceptions') as UntypedFormArray;
  }

  newException(exception, schIndex): UntypedFormGroup {
    const schedule = this.schedules[schIndex];
    const exceptionDay = moment(exception.date).tz(this.timezone)
      .format('ddd');
    const scheduleDayHours = schedule.hours[exceptionDay];

    if (exception.hours[0][0] === '') {
      exception.hours = scheduleDayHours.hours;
    }

    const form = this.fb.group(
      {
        _id: [exception._id],
        date: [exception.date],
        closed: [exception.closed],
        nextDay: [exception.nextDay || false],
        publicDisabled: [exception.publicDisabled || false],
        hoursFirstValueFirstArray: [exception.hours[0][0]],
        hoursSecondValueFirstArray: [exception.hours[0][1]],
      },
      { validators: [timepickerValidator, exceptionValidator(this.schedules, schIndex)] },
    );

    if (exception.hours[0][0] !== '' && !exception._id) {
      form.markAsDirty();
    }

    return form;
  }

  addException(schIndex: number, exception) {
    this.exceptionsArray(schIndex).push(this.newException(exception, schIndex));
  }

  removeException(schIndex: number, excIndex: number) {
    this.exceptionsArray(schIndex).removeAt(excIndex);
  }

  editHours(schIndex, schedule, extraDate) {
    this.showExceptions[schIndex] = true;
    this.showCalendar[schIndex] = false;
    const hours = [['', '']];
    const date = moment.tz(extraDate, 'D-M-YYYY', this.timezone).format('MMM D, YYYY');

    const data = {
      date,
      hours,
      closed: false,
      publicDisabled: false,
    };
    this.extraDate = '';
    this.schedulesArray.controls[schIndex - 1].patchValue({ 'extraDate': '' });

    const exceptionsWithTheSameDate = this.schedulesArray.at(schIndex - 1).get('exceptions').value.filter(item => date === item.date);
    if (exceptionsWithTheSameDate.length === 2) {
      this.alertsService.add({
        type: IAlertTypes.warning,
        heading: 'Warning: ',
        body: 'Don\'t allow to add the third exception for the same day.',
      });

      return;
    }
    this.addException(schIndex - 1, data);
  }

  removeExtraDay(schIndex, schedule, excIndex) {
    if (!this.checkBeforeRemove(schedule, excIndex)) {
      return;
    }
    this.removeException(schIndex, excIndex);
    this.updateSchedule(schedule);
  }

  checkBeforeRemove(schedule, excIndex): boolean {
    const removedException: IException[] = schedule.exceptions.splice(excIndex, 1);
    this.updateExceptionsModel();

    const indicesInvalidSchedules = this.getIndicesInvalidSchedules(this.restaurantHoursForm.get('schedules').value);

    if (indicesInvalidSchedules?.length && schedule.default) {
      indicesInvalidSchedules.forEach(i => this.showExceptions[i + 1] = true);
      // Insert removed value back
      schedule.exceptions.splice(excIndex, 0, ...removedException);

      return false;
    }

    return true;
  }

  getIndicesInvalidSchedules(schedules: IScheduleForm[]) {
    return schedules.reduce((indices, current, i) => {
      if (this.restaurantHoursForm.get(`schedules.${i}.exceptions`).status === 'INVALID') {
        indices.push(i);
      }

      return indices;
    }, []);
  }

  addEditSchedule(schedule = {}) {
    this.modalController.create<AddEditScheduleComponent, IScheduleForm>(
      AddEditScheduleComponent,
      {
        schedule,
        schedules: this.schedules,
      },
      FrontendModalSize.wider,
    )
      .pipe(
        take(1),
        filter(data => data !== undefined),
        tap((res) => {
          this.updateSchedule(res);
        }),
      )
      .subscribe();
  }

  deleteSchedule(schedule: IScheduleForm): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(
      ConfirmationModalComponent,
      {
        options: {
          title: 'Delete schedule',
          content: `Are you sure you want to delete ${schedule.name}?`,
        },
      },
    )
      .pipe(
        take(1),
        filter(res => res?.response),
        switchMap(() => this.scheduleApiService.deleteById(schedule._id)),
        tap(() => {
          this.alertsService.addSuccess('deleted schedule.', 'Deleted: ');
        }),
        catchError((error) => {
          this.alertsService.addError(String(error.error.error), 'Error: ');

          return of(null);
        }),
      )
      .subscribe();
  }

  updateSchedule(editedSchedule: IScheduleForm, exceptionControl?: UntypedFormArray, excIndex?: number) {
    const schedule = cloneDeep(editedSchedule),
      exceptions: IExceptionForm[] = exceptionControl?.value;

    if (!this.schedules.length) {
      schedule.default = true;
    }
    this.scheduleService.convertHours(schedule.hours, this.timezone);
    this.scheduleService.checkCloseDays(schedule.hours);

    if (exceptions?.length) {
      exceptionControl.controls[excIndex].markAsPristine();
      const scheduleException = schedule.exceptions[excIndex],
        exception = exceptions[excIndex],
        exceptionsWithTheSameDate = exceptions.filter(item => exception?.date === item.date);

      if (exceptionsWithTheSameDate.length === 2 && (exceptionsWithTheSameDate[0].closed || exceptionsWithTheSameDate[1].closed || exceptionsWithTheSameDate[0].publicDisabled || exceptionsWithTheSameDate[1].publicDisabled)) {
        const closedType = exceptionsWithTheSameDate[0].publicDisabled || exceptionsWithTheSameDate[1].publicDisabled ? 'public disabled' : 'closed';

        this.alertsService.add({
          type: IAlertTypes.warning,
          heading: 'Warning: ',
          body: `Don't allow to save the ${closedType} exception for the same day.`,
        });

        return;
      }
      if (scheduleException) {
        scheduleException.hours = [[exception.hoursFirstValueFirstArray, exception.hoursSecondValueFirstArray]];
        scheduleException.closed = exception.closed;
        scheduleException.nextDay = exception.closed ? false : exception.nextDay;
        scheduleException.publicDisabled = exception.publicDisabled || false;
      } else {
        const newException = {
          date: exception.date,
          hours: [[exception.hoursFirstValueFirstArray, exception.hoursSecondValueFirstArray]],
          closed: exception.closed,
          nextDay: exception.closed ? false : exception.nextDay,
          publicDisabled: exception.publicDisabled || false,
        };
        schedule.exceptions.push(newException);
      }
    }
    if (schedule.exceptions?.length) {
      this.scheduleService.convertHours(schedule.exceptions, this.timezone);
      this.scheduleService.convertDates(schedule.exceptions, this.timezone);
      schedule.exceptions.forEach((e) => {
        this.scheduleService.checkCloseDays(e);
      });
    }

    of(schedule as Schedule)
      .pipe(
        switchMap((schedule) => {
          if (schedule._id) {
            const { _id, ...data } = schedule;
            return this.scheduleApiService.updateById(_id, data);
          }

          return this.scheduleApiService.create(schedule);
        }),
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: 'updated schedule',
          });
        }),
        catchError((error) => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: String(error.error.error),
          });

          return of(null);
        }),
      )
      .subscribe();
  }

  showHideExceptions(schIndex: number): void {
    this.showExceptions[schIndex + 1] = !this.showExceptions[schIndex + 1];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
